import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../config/api';
import { parseSignalkNavigation } from '../utils/signalKValueFormat';

export const CHART_ZOOM = 14;

/**
 * Resolve vessel GPS from live stream cache → signalk_latest → last known position.
 */
export function useVesselPosition(initialVesselId = null) {
  const [vesselId, setVesselId] = useState(initialVesselId);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState(null);
  const vesselIdRef = useRef(initialVesselId);
  const lastStreamAtRef = useRef(0);

  // While the WebSocket stream is delivering fixes, the REST poll must not
  // overwrite them — its cached values lag behind and make the marker
  // bounce between current and stale positions.
  const STREAM_FRESH_MS = 30000;

  useEffect(() => {
    vesselIdRef.current = vesselId;
  }, [vesselId]);

  const resolveVesselId = useCallback(async () => {
    if (vesselIdRef.current) return vesselIdRef.current;
    const resp = await api.get('/api/vessels');
    const vessels = resp.data?.vessels ?? (Array.isArray(resp.data) ? resp.data : []);
    const vid = vessels[0]?.id ?? null;
    if (vid) {
      vesselIdRef.current = vid;
      setVesselId(vid);
    }
    return vid;
  }, []);

  const fetchPosition = useCallback(async () => {
    try {
      const vid = await resolveVesselId();
      if (!vid) {
        setLoading(false);
        return null;
      }

      if (Date.now() - lastStreamAtRef.current < STREAM_FRESH_MS) {
        return null; // live stream owns the position right now
      }

        try {
        const live = await api.get(`/api/vessels/${vid}/live-position`);
        if (live.data?.latitude != null && live.data?.longitude != null) {
          setLocation((prev) => ({
            latitude: live.data.latitude,
            longitude: live.data.longitude,
            heading: prev?.heading ?? 0,
            speed: prev?.speed ?? 0,
            ...(live.data.depth_meters != null ? { depth: live.data.depth_meters } : {}),
          }));
          setSource(live.data.source || 'live');
          setLoading(false);
          return {
            latitude: live.data.latitude,
            longitude: live.data.longitude,
          };
        }
      } catch {
        /* try next source */
      }

      try {
        const latest = await api.get(`/api/vessels/${vid}/signalk/latest`);
        const nav = parseSignalkNavigation(latest.data);
        if (nav?.latitude != null && nav?.longitude != null) {
          const fix = {
            latitude: nav.latitude,
            longitude: nav.longitude,
            heading: nav.heading ?? 0,
            speed: nav.speed ?? 0,
            ...(nav.depth != null ? { depth: nav.depth } : {}),
          };
          setLocation(fix);
          setSource('signalk_latest');
          setLoading(false);
          return fix;
        }
      } catch {
        /* try next source */
      }

      try {
        const last = await api.get(`/api/vessels/${vid}/last-position`);
        if (last.data?.latitude != null && last.data?.longitude != null) {
          setLocation((prev) => ({
            latitude: last.data.latitude,
            longitude: last.data.longitude,
            heading: prev?.heading ?? 0,
            speed: prev?.speed ?? 0,
          }));
          setSource(last.data.source || 'last');
          setLoading(false);
          return {
            latitude: last.data.latitude,
            longitude: last.data.longitude,
          };
        }
      } catch {
        /* no position */
      }

      setLoading(false);
      return null;
    } catch (err) {
      console.error('[useVesselPosition] fetch failed:', err);
      setLoading(false);
      return null;
    }
  }, [resolveVesselId]);

  const updatePosition = useCallback((partial) => {
    if (!partial) return;
    setLocation((prev) => {
      const next = {
        latitude: partial.latitude ?? prev?.latitude,
        longitude: partial.longitude ?? prev?.longitude,
        heading: partial.heading ?? prev?.heading ?? 0,
        speed: partial.speed ?? prev?.speed ?? 0,
      };
      if (next.latitude == null || next.longitude == null) return prev;
      // No actual change → keep the same object so React skips the re-render
      if (
        prev &&
        prev.latitude === next.latitude &&
        prev.longitude === next.longitude &&
        prev.heading === next.heading &&
        prev.speed === next.speed
      ) {
        return prev;
      }
      return next;
    });
    if (partial.latitude != null && partial.longitude != null) {
      lastStreamAtRef.current = Date.now();
      setSource('stream');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosition();
    const interval = setInterval(fetchPosition, 15000);
    return () => clearInterval(interval);
  }, [fetchPosition]);

  return {
    vesselId,
    setVesselId,
    vesselIdRef,
    location,
    loading,
    source,
    fetchPosition,
    updatePosition,
    setLocation,
  };
}
